/**
 * Access / Security Auditor — a top-level admin tab. One probe over every saved
 * connection (server-side, bounded-parallel) audits its MySQL accounts for risk:
 * anonymous users, empty passwords, wildcard hosts, remotely-reachable root, and
 * over-broad global privileges. Read-only — nothing is changed; a connection
 * whose login can't read mysql.user shows an honest "insufficient privilege" row.
 */
import { createSignal, createResource, createMemo, createEffect, onCleanup, For, Show } from 'solid-js';
import { probeAccessAudit } from '../lib/accessaudit.js';

const AUTO_REFRESH_MS = 60000;

const SEV_LABEL = { high: 'High', medium: 'Medium', low: 'Low' };

export default function AccessAuditPanel() {
  const [data, { refetch }] = createResource(probeAccessAudit);
  const [auto, setAuto] = createSignal(false);
  const [group, setGroup] = createSignal('');
  const [expandedId, setExpandedId] = createSignal(0);

  createEffect(() => {
    if (!auto()) return;
    const t = setInterval(() => { if (!data.loading) refetch(); }, AUTO_REFRESH_MS);
    onCleanup(() => clearInterval(t));
  });

  const conns = () => data()?.connections || [];
  const groups = createMemo(() => {
    const set = new Set(conns().map((c) => c.groupName || 'Ungrouped'));
    return [...set].sort((a, b) => (a === 'Ungrouped' ? 1 : b === 'Ungrouped' ? -1 : a < b ? -1 : 1));
  });
  const visible = createMemo(() =>
    group() ? conns().filter((c) => (c.groupName || 'Ungrouped') === group()) : conns());

  const probedAt = () => {
    const t = data()?.probedAt;
    return t ? new Date(t * 1000).toLocaleTimeString() : '';
  };

  const riskCount = (c) => (c.risky?.length || 0);
  const worst = (c) => {
    if (c.counts?.high) return 'high';
    if (c.counts?.medium) return 'medium';
    if (c.counts?.low) return 'low';
    return '';
  };
  const toggleExpand = (c) => {
    if (!riskCount(c)) return;
    setExpandedId(expandedId() === c.connId ? 0 : c.connId);
  };

  // Fleet totals across all reachable connections.
  const totals = createMemo(() => {
    const t = { high: 0, medium: 0, low: 0, accounts: 0 };
    for (const c of conns()) {
      t.high += c.counts?.high || 0;
      t.medium += c.counts?.medium || 0;
      t.low += c.counts?.low || 0;
      t.accounts += riskCount(c);
    }
    return t;
  });

  return (
    <div class="fhealth-panel">
      <div class="fhealth-head">
        <div>
          <h2>Access / Security Auditor</h2>
          <p class="muted">Live audit of MySQL accounts across every connection — empty passwords, wildcard hosts, remote root, over-broad grants.</p>
        </div>
        <div class="fhealth-actions">
          <Show when={probedAt()}><span class="muted fhealth-stamp">probed {probedAt()}</span></Show>
          <label class="fhealth-auto">
            <input type="checkbox" checked={auto()} onInput={(e) => setAuto(e.currentTarget.checked)} />
            Auto-refresh
          </label>
          <button class="btn" disabled={data.loading} onClick={refetch}>
            {data.loading ? 'Auditing…' : '⟳ Refresh'}
          </button>
        </div>
      </div>

      <Show when={!data.loading && conns().length}>
        <div class="aa-summary">
          <Show when={totals().accounts === 0}>
            <span class="aa-allclear">✓ No risky accounts found across the fleet.</span>
          </Show>
          <Show when={totals().high > 0}><span class="aa-pill aa-pill-high">{totals().high} high</span></Show>
          <Show when={totals().medium > 0}><span class="aa-pill aa-pill-medium">{totals().medium} medium</span></Show>
          <Show when={totals().low > 0}><span class="aa-pill aa-pill-low">{totals().low} low</span></Show>
        </div>
      </Show>

      <Show when={groups().length > 1}>
        <div class="fhealth-chips">
          <button class={`day-chip ${group() === '' ? 'on' : ''}`} onClick={() => setGroup('')}>All</button>
          <For each={groups()}>
            {(g) => <button class={`day-chip ${group() === g ? 'on' : ''}`} onClick={() => setGroup(g)}>{g}</button>}
          </For>
        </div>
      </Show>

      <Show when={!data.loading || data()} fallback={<div class="muted">Auditing the fleet…</div>}>
        <table class="jobs-table fhealth-table">
          <thead>
            <tr>
              <th></th>
              <th>Connection</th>
              <th>Server</th>
              <th>Risky accounts</th>
              <th>Clean</th>
              <th>Severity</th>
            </tr>
          </thead>
          <tbody>
            <For each={visible()}>
              {(c) => (
                <>
                  <tr
                    class={`fhealth-row ${riskCount(c) ? 'fhealth-expandable' : ''} ${expandedId() === c.connId ? 'fhealth-open' : ''}`}
                    onClick={() => toggleExpand(c)}
                  >
                    <td><span class={`fhealth-dot ${c.reachable ? (riskCount(c) ? `aa-dot-${worst(c)}` : 'fhealth-dot-ok') : 'fhealth-dot-bad'}`} /></td>
                    <td>
                      <span class="fhealth-name">
                        <Show when={c.color}><span class="conn-swatch" style={`background:${c.color}`} /></Show>
                        {c.name}
                      </span>
                      <Show when={c.groupName}><span class="muted fhealth-group"> · {c.groupName}</span></Show>
                    </td>
                    <Show
                      when={c.reachable}
                      fallback={<td colSpan={4} class="fhealth-offline-msg">offline — {c.error || 'unreachable'}</td>}
                    >
                      <Show
                        when={!c.privError}
                        fallback={<td colSpan={4} class="fhealth-offline-msg">{c.server?.display} · insufficient privilege — {c.privError}</td>}
                      >
                        <td>{c.server?.display || '—'}</td>
                        <td>
                          <Show when={riskCount(c)} fallback={<span class="aa-ok">none</span>}>
                            <strong>{riskCount(c)}</strong>
                          </Show>
                        </td>
                        <td class="muted">{c.cleanCount}</td>
                        <td>
                          <Show when={c.counts?.high}><span class="aa-pill aa-pill-high">{c.counts.high} high</span></Show>
                          <Show when={c.counts?.medium}><span class="aa-pill aa-pill-medium">{c.counts.medium} med</span></Show>
                          <Show when={c.counts?.low}><span class="aa-pill aa-pill-low">{c.counts.low} low</span></Show>
                          <Show when={!riskCount(c)}><span class="muted">—</span></Show>
                        </td>
                      </Show>
                    </Show>
                  </tr>
                  <Show when={expandedId() === c.connId && riskCount(c)}>
                    <tr class="fhealth-drill-row">
                      <td></td>
                      <td colSpan={5}><AccountDrill accounts={c.risky} /></td>
                    </tr>
                  </Show>
                </>
              )}
            </For>
          </tbody>
        </table>
        <Show when={!visible().length}><div class="muted">No connections.</div></Show>
      </Show>
    </div>
  );
}

function AccountDrill(props) {
  return (
    <div class="aa-drill">
      <For each={props.accounts}>
        {(a) => (
          <div class={`aa-account aa-account-${a.maxSeverity}`}>
            <div class="aa-acct-head">
              <span class="mono aa-acct-name">'{a.user}'@'{a.host}'</span>
              <span class={`aa-pill aa-pill-${a.maxSeverity}`}>{SEV_LABEL[a.maxSeverity]}</span>
            </div>
            <ul class="aa-findings">
              <For each={a.findings}>
                {(f) => (
                  <li class={`aa-finding aa-finding-${f.severity}`}>
                    <span class={`aa-sev-dot aa-sev-${f.severity}`} />
                    {f.detail}
                  </li>
                )}
              </For>
            </ul>
          </div>
        )}
      </For>
    </div>
  );
}
